import FloatyText from "./FloatyText";
import HyperlinkText from "./HyperlinkText";
import RichTextTokenParser from "./RichTextTokenParser";
import { UnderlinedTextDecorator } from "./UnderlinedTextDecorator";

export default class RichTextRenderer {
    constructor(styling, container, rtl, maxWidth) {
        this._styling = styling;
        this._container = container;
        this._rtl = rtl || false;
        this._maxWidth = maxWidth || 0;

        this._currentLine = 0;
        this._currentX = 0;
        this._textHeight = 0;
        this._textColor = "#000";

        this._displayObjects = [];
    }

    destroy() {
        this._displayObjects.forEach(function (obj) {
            if (obj.parent != null) {
                obj.parent.removeChild(obj);
            }
        });
    }

    renderLines(linesData, align, lineHeight, color) {
        linesData.forEach(function (lineData) {
            this.renderLine(lineData, align, lineHeight, color);
        }.bind(this));
    }

    renderLine(lineData, align, lineHeight, color) {
        this._textHeight = lineHeight;
        if (color) this._textColor = color;

        let wordsToRender = lineData.wordsData;
        
        if (this._rtl) {
            // Crude RTL implementation: simply reverse word order
            // NOT SUPPORTED: Mixed bidirectional text (e.g., Arabic text with embedded English phrases)
            // Assumes text is either fully RTL or fully LTR, not mixed within the same line
            // For true BiDi support, would need Unicode BiDi algorithm implementation
            wordsToRender = lineData.wordsData.slice().reverse();
            
            // Fix punctuation placement: merge standalone punctuation with following word
            // (which was the preceding word before reversal)
            wordsToRender = this._mergePunctuationRTL(wordsToRender);
        }
        
        // Use the same alignment logic for both RTL and LTR.
        // The container is positioned at the text field's origin, and textAlign determines the offset.
        // For RTL, words are rendered in reversed order, but the alignment calculation is identical.
        this._currentX = this._computeStartX(lineData, align);
        
        wordsToRender.forEach(function (wordData) {
            var token = RichTextTokenParser.parse(wordData);
            this.renderWord(token);
            this._currentX += token.width;
        }.bind(this));

        this._currentX = 0;
        this._currentLine++;
    }

    renderWord(token) {
        var textInstance;
        switch (token.tag) {
            case 'a':
                textInstance = this.renderAnchorText(token);
                break;
            case 'b':
                textInstance = this.renderBoldText(token);
                break;
            case 'i':
                textInstance = this.renderItalicText(token);
                break;
            case 'u':
                textInstance = this.renderUnderlinedText(token);
                break;
            case 'bi':
            case 'ib':
                textInstance = this.renderBoldItalicText(token);
                break;
            case 'floaty':
                textInstance = this.renderFloatyText(token);
                break;
            case undefined:
            default:
                textInstance = this.renderTextDefault(token);
                break;
        }
        this._displayObjects.push(textInstance);
    }

    renderAnchorText(token) {
        var color = token.attributes.color || this._styling.a.color || this._textColor;
        var renderedText = new HyperlinkText(token.text, this._styling.a.font, color, this._styling.a, token.attributes.href);
        this._addRenderedTextToContainer(renderedText);
        UnderlinedTextDecorator(renderedText)();
        renderedText.updateHitArea();
        this._displayObjects.push(renderedText.underline);

        return renderedText;
    }

    renderBoldText(token) {
        var renderedText = new createjs.Text(token.text, this._styling.b.font, token.attributes.color || this._textColor);
        this._addRenderedTextToContainer(renderedText);

        return renderedText;
    }

    renderItalicText(token) {
        var renderedText = new createjs.Text(token.text, this._styling.i.font, token.attributes.color || this._textColor);
        this._addRenderedTextToContainer(renderedText);

        return renderedText;
    }

    renderBoldItalicText(token) {
        var renderedText = new createjs.Text(token.text, this._styling.bi.font, token.attributes.color || this._textColor);
        this._addRenderedTextToContainer(renderedText);

        return renderedText;
    }

    renderUnderlinedText(token) {
        var renderedText = new createjs.Text(token.text, this._styling._default.font, token.attributes.color || this._textColor);
        this._addRenderedTextToContainer(renderedText);
        UnderlinedTextDecorator(renderedText)();
        this._displayObjects.push(renderedText.underline);

        return renderedText;
    }

    renderFloatyText(token) {
        var renderedText = new FloatyText(token.text, this._styling._default.font, token.attributes.color || this._textColor);
        this._addRenderedTextToContainer(renderedText);

        return renderedText;
    }

    renderTextDefault(token) {
        var renderedText = new createjs.Text(token.text, this._styling._default.font, token.attributes.color || this._textColor);
        this._addRenderedTextToContainer(renderedText);
        return renderedText;
    }

    _computeStartX(lineData, align) {
        switch (align) {
            case "right":
                return -lineData.width;
            case "center":
                return -lineData.width / 2;
            case "left":
            default:
                return 0;
        }
    }

    /**
     * Merge standalone punctuation tokens with the following word for RTL rendering.
     * After reversal, punctuation that should trail a word appears before it.
     * This merges them back together so punctuation renders in the correct position.
     * 
     * Rule: Punctuation (. : ، ؛ ؟ ! ?) should attach to the RTL word it logically follows.
     */
    _mergePunctuationRTL(words) {
        const punctuationChars = /^[.:،؛؟!?]+$/;
        const result = [];
        
        for (let i = 0; i < words.length; i++) {
            const word = words[i];
            const trimmed = word.text.trim();
            
            // Check if this token is pure punctuation
            if (trimmed && punctuationChars.test(trimmed)) {
                // Merge with the next word (which was originally before this punctuation)
                if (i + 1 < words.length) {
                    const nextWord = words[i + 1];
                    // Append punctuation to the end of next word
                    nextWord.text = nextWord.text + word.text;
                    nextWord.width = nextWord.width + word.width;
                    // Skip this punctuation token (it's now merged)
                    continue;
                }
            }
            
            result.push(word);
        }
        
        return result;
    }

    _addRenderedTextToContainer(renderedText) {
        renderedText.x = this._currentX;
        renderedText.y = this._currentLine * this._textHeight;
        
        // Set canvas direction for RTL to fix punctuation placement
        // Note: We only set ctx.direction, not the full RTLTextPatch behavior,
        // because we're manually positioning words and don't want the translate/textAlign transforms
        if (this._rtl) {
            const originalDraw = renderedText.draw;
            renderedText.draw = function(ctx, ignoreCache) {
                ctx.save();
                ctx.direction = 'rtl';
                const result = originalDraw.call(this, ctx, ignoreCache);
                ctx.restore();
                return result;
            };
        }

        this._container.addChild(renderedText);
    }
}